import React, { useState, useEffect } from "react";
import tw from "twin.macro";
import styled, { keyframes } from "styled-components";
import { css } from "styled-components/macro"; //eslint-disable-line

// ===== Asset imports (adjust alias if necessary) =====
import alpacaLogo from "images/exchanges/alpaca.svg";
import binanceLogo from "images/exchanges/binance.svg";
import binanceUsLogo from "images/exchanges/binance.us.svg";
import bingxLogo from "images/exchanges/bingx.svg";
import bitfinexLogo from "images/exchanges/bitfinex.svg";
import bitgetLogo from "images/exchanges/bitget.svg";
import bitstampLogo from "images/exchanges/bitstamp.svg";
import bittrexLogo from "images/exchanges/bittrex.svg";
import bybitLogo from "images/exchanges/bybit.svg";
import cexLogo from "images/exchanges/cex.svg";
import coinbaseLogo from "images/exchanges/gdax.svg";
import cryptoComLogo from "images/exchanges/crypto.com.svg";
import dydxLogo from "images/exchanges/dydx.svg";
import futuresBinanceLogo from "images/exchanges/futures_binance.svg";
import gateioLogo from "images/exchanges/gateio.svg";
import hitbtcLogo from "images/exchanges/hitbtc.svg";
import huobiLogo from "images/exchanges/huobi.svg";
import hyperliquidLogo from "images/exchanges/hyperliquid.svg";
import krakenLogo from "images/exchanges/kraken.svg";
import krakenFuturesLogo from "images/exchanges/krakenfutures.svg";
import kucoinLogo from "images/exchanges/kucoin.svg";
import mexcLogo from "images/exchanges/mexc.svg";
import okxLogo from "images/exchanges/okx.svg";
import pancakeLogo from "images/exchanges/pancake.svg";
import poloniexLogo from "images/exchanges/poloniex.svg";

// —————————————————————————————————————————————————————
// 🎛  Infinite two‐row marquee with edge‐fade mask
// —————————————————————————————————————————————————————

const DURATION = 150; // seconds per full loop

const scroll = keyframes`
  0%   { transform: translateX(0); }
  100% { transform: translateX(-50%); }
`;

// outer container + fade mask
const CarouselSection = styled.section`
  ${tw`w-full pt-12 relative overflow-hidden select-none`};
  mask-image: linear-gradient(to right, transparent 0%, black 10%, black 90%, transparent 100%);
  -webkit-mask-image: linear-gradient(to right, transparent 0%, black 10%, black 90%, transparent 100%);
`;

// base track
const Track = styled.div`
  display: inline-flex;
  align-items: center;
  width: max-content;
  animation: ${scroll} ${DURATION}s linear infinite;
  will-change: transform;
  animation-play-state: ${({ $isRunning }) => ($isRunning ? "running" : "paused")};
`;

const TrackReverse = styled(Track)`
  margin-top: 2rem;
  animation-direction: reverse;
  animation-delay: -${DURATION / 2}s;
`;

// uniform logo slot
const LogoSlot = styled.div`
  ${tw`flex items-center justify-center mx-4`};
  width: 10rem;
  height: 2rem;
  flex: 0 0 auto;
`;

// uniform logo image
const LogoImg = styled.img`
  height: 1.25rem;
  width: auto;
  object-fit: contain;
  filter: brightness(0) invert(0.75) opacity(0.8);
`;

// —————————————————————————————————————————————————————
// data + split logic
// —————————————————————————————————————————————————————

const allLogos = [
  { src: alpacaLogo, name: "Alpaca" },
  { src: binanceLogo, name: "Binance" },
  { src: binanceUsLogo, name: "Binance US" },
  { src: bingxLogo, name: "BingX" },
  { src: bitfinexLogo, name: "Bitfinex" },
  { src: bitgetLogo, name: "Bitget" },
  { src: bitstampLogo, name: "Bitstamp" },
  { src: bittrexLogo, name: "Bittrex" },
  { src: bybitLogo, name: "Bybit" },
  { src: cexLogo, name: "CEX" },
  { src: coinbaseLogo, name: "Coinbase" },
  { src: cryptoComLogo, name: "Crypto.com" },
  { src: dydxLogo, name: "dYdX" },
  { src: futuresBinanceLogo, name: "Binance Futures" },
  { src: gateioLogo, name: "Gate.io" },
  { src: hitbtcLogo, name: "HitBTC" },
  { src: huobiLogo, name: "Huobi" },
  { src: hyperliquidLogo, name: "Hyperliquid" },
  { src: krakenLogo, name: "Kraken" },
  { src: krakenFuturesLogo, name: "Kraken Futures" },
  { src: kucoinLogo, name: "KuCoin" },
  { src: mexcLogo, name: "MEXC" },
  { src: okxLogo, name: "OKX" },
  { src: pancakeLogo, name: "PancakeSwap" },
  { src: poloniexLogo, name: "Poloniex" },
];

// split into two disjoint sets by index parity
const row1 = allLogos.filter((_, i) => i % 2 === 0);
const row2 = allLogos.filter((_, i) => i % 2 !== 0);

// duplicate for seamless loop
const loop1 = [...row1, ...row1];
const loop2 = [...row2, ...row2];

// —————————————————————————————————————————————————————
// component
// —————————————————————————————————————————————————————

const ExchangeCarousel = () => {
  const [isRunning, setIsRunning] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsRunning(true), 2500);
    return () => clearTimeout(timer);
  }, []);

  return (
    <CarouselSection role="region" aria-label="Supported exchanges carousel">
      <Track $isRunning={isRunning}>
        {loop1.map((logo, i) => (
          <LogoSlot key={`r1-${i}`}>
            <LogoImg
              src={logo.src}
              alt={`${logo.name} logo`}
              loading="lazy"
              draggable={false}
            />
          </LogoSlot>
        ))}
      </Track>
      <TrackReverse $isRunning={isRunning}>
        {loop2.map((logo, i) => (
          <LogoSlot key={`r2-${i}`}>
            <LogoImg
              src={logo.src}
              alt={`${logo.name} logo`}
              loading="lazy"
              draggable={false}
            />
          </LogoSlot>
        ))}
      </TrackReverse>
    </CarouselSection>
  );
};

export default React.memo(ExchangeCarousel);
